import { PartyTypeProps, partyTypes } from "./partyType";

// The party types that are machining processes. Each one has a rate per item, kept as "{type}_rate":
// the item's own rate on the item page, and each party's rate on that party's ભાવ page.
// Keep in step with Parties::PROCESS_TYPES on the backend.
const processTypeValues = ["cutting", "turning", "vmc"];

export type RateField = "cutting_rate" | "turning_rate" | "vmc_rate";

export const processTypes: PartyTypeProps[] = partyTypes.filter((type) =>
  processTypeValues.includes(type.value)
);

export const rateField = (type: string) => `${type}_rate` as RateField;

export const rateLabel = (type: PartyTypeProps) => `${type.label} ભાવ`;

// The process types a party has, in the order above. Only these get a rate box for that party.
export const partyProcessTypes = (types: string[] | null | undefined) =>
  processTypes.filter((type) => !!types?.includes(type.value));

// What a rate box keeps of the typed text: digits and one dot, up to 8 whole digits and 2 decimals.
export const sanitizeRate = (text: string) => {
  const [whole, ...decimals] = text.replace(/[^\d.]/g, "").split(".");

  return decimals.length > 0
    ? `${whole.slice(0, 8)}.${decimals.join("").slice(0, 2)}`
    : whole.slice(0, 8);
};

// A rate box's text as sent to the server. A blank box, or a lone ".", has no rate; 0 is a rate.
export const rateNumber = (text: string | null | undefined): number | null => {
  const value = (text ?? "").trim();

  if (value === "" || value === ".") return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const hasRate = (text: string | null | undefined) =>
  rateNumber(text) !== null;

// A saved rate as a rate box's text.
export const rateText = (value: number | null | undefined) =>
  value === null || value === undefined ? "" : String(value);

export const formatRate = (value: number | null | undefined) =>
  value === null || value === undefined
    ? "-"
    : value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

// Item codes in reading order, so AB-2 comes before AB-10.
export const compareCodes = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
